/********************************************************* {COPYRIGHT-TOP} ***
 * Licensed Materials - Property of IBM
 *
 * (C) Copyright IBM Corporation 2021, 2024
 *
 * All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 ********************************************************** {COPYRIGHT-END} **/
import { useState, useEffect, useCallback } from 'react';
import { useApi } from '../contexts/ApiHandler';
import { useTranslation } from 'react-i18next';
import { snippetFactory } from './snippetFactory';
import { codeLanguages } from './constants';
import { isEmpty } from 'lodash';

import JavaKafkaCodeSnippet from './KafkaSnippets/JavaKafkaCodeSnippet';
import KafkaJSKafkaCodeSnippet from './KafkaSnippets/KafkaJSKafkaCodeSnippet';
import NoderdKafkaCodeSnippet from './KafkaSnippets/PythonKafkaCodeSnippet';
import PythonKafkaCodeSnippet from './KafkaSnippets/NoderdKafkaCodeSnippet';
import KafkacatKafkaCodeSnippet from './KafkaSnippets/KafkacatKafkaCodeSnippet';
import KafkaConsoleConsumerCodeSnippet from './KafkaSnippets/KafkaConsoleConsumerCodeSnippet';
import { useConfig } from 'contexts/config';

const useGenerateKafkaSnippet = ({ apiType, codeLanguage, channelName, selectedCredentials, operationId }) => {
  const { apiHandler } = useApi();
  const { t } = useTranslation();
  const { options } = useConfig();

  const [generatedSnippetOptions, updateGeneratedSnippetOptions] = useState({});
  const [generatedSnippet, updateGeneratedSnippet] = useState({ snippet: '', dependencies: '' });
  const getClassForLanguage = useCallback(
    (codeLanguage) => {
      const constructorValues = [apiHandler, channelName, selectedCredentials, t, options.loggedIn, operationId];
      switch (codeLanguage) {
        case codeLanguages.java:
          return new JavaKafkaCodeSnippet(...constructorValues);
        case codeLanguages.consoleConsumer:
          return new KafkaConsoleConsumerCodeSnippet(...constructorValues);
        case codeLanguages.kafkaJS:
          return new KafkaJSKafkaCodeSnippet(...constructorValues);
        case codeLanguages.noderdKafka:
          return new NoderdKafkaCodeSnippet(...constructorValues);
        case codeLanguages.kafkaPython:
          return new PythonKafkaCodeSnippet(...constructorValues);
        case codeLanguages.kafkacat:
          return new KafkacatKafkaCodeSnippet(...constructorValues);
        default: {
          throw new Error(`Snippet options for ${codeLanguage} is not supported`);
        }
      }
    },
    [apiHandler, channelName, selectedCredentials, t, options.loggedIn, operationId]
  );

  useEffect(() => {
    const snippetLanguage = getClassForLanguage(codeLanguage);
    updateGeneratedSnippetOptions({
      channel: channelName,
      operationBindings: snippetLanguage.getOperationBindings(),
      securityDetails: snippetLanguage.getSecurityDetails(),
      bootstrapServers: snippetLanguage.getBootstrapServers(),
      schema: snippetLanguage.getSchemaInformation(),
      kafkaKey: snippetLanguage.getKeyDeserializer(),
      applicationDetails: snippetLanguage.getApplicationDetails(),
      encodingType: snippetLanguage.getEncodingType(),
      enforced: snippetLanguage.getEnforcedType()
    });
  }, [apiHandler, channelName, selectedCredentials, codeLanguage, getClassForLanguage, operationId]);

  useEffect(() => {
    if (!isEmpty(generatedSnippetOptions)) {
      updateGeneratedSnippet(snippetFactory(apiType)(codeLanguage, generatedSnippetOptions));
    }
  }, [generatedSnippetOptions, apiType, codeLanguage]);

  return {
    snippet: generatedSnippet.snippet,
    dependencies: generatedSnippet.dependencies
  };
};

export default useGenerateKafkaSnippet;
